"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/fun-ui/Button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGlobalImageGenerationSocket } from "@/features/research-drawing/ImageGenerationSocketContext";
import type { ImageGenerationTask } from "@/features/research-drawing/types";
import {
  isDesktopNotificationRuntime,
  openDesktopNotificationSettings,
} from "@/lib/desktop/notifications";
import { useResearchDrawing, useStore } from "@/stores/appStore";
import {
  getTrackedResearchDrawingTaskIds,
  getTaskNotificationSettings,
  publishTaskNotification,
  trackResearchDrawingTask,
  untrackResearchDrawingTask,
  createTaskNotificationDeduper,
  type TaskNotificationStatus,
} from "@/lib/task-notifications";

const DESKTOP_NOTIFICATION_SETTINGS_PROMPTED_KEY = "findin:desktop-notification-settings-prompted";

function shouldPromptDesktopNotificationSettings() {
  const settings = getTaskNotificationSettings();
  return typeof window !== "undefined"
    && isDesktopNotificationRuntime()
    && settings.desktopNotificationMode !== "never"
    && window.localStorage.getItem(DESKTOP_NOTIFICATION_SETTINGS_PROMPTED_KEY) !== "1";
}

function markDesktopNotificationSettingsPrompted() {
  window.localStorage.setItem(DESKTOP_NOTIFICATION_SETTINGS_PROMPTED_KEY, "1");
}

function isResearchDrawingPage(pathname: string | null) {
  return pathname === "/research-drawing";
}

export function GlobalTaskNotificationBridge() {
  const pathname = usePathname();
  const { state } = useGlobalImageGenerationSocket();
  const { fetchHistory } = useResearchDrawing();
  const [showDesktopNotificationSettings, setShowDesktopNotificationSettings] = useState(false);
  const [openingDesktopNotificationSettings, setOpeningDesktopNotificationSettings] = useState(false);
  const terminalNotificationDeduperRef = useRef(createTaskNotificationDeduper());

  const notifyTerminalTask = useCallback((taskId: string, status: TaskNotificationStatus, error?: { code?: number; type?: string }) => {
    const notificationKey = `research-drawing:${taskId}:${status}`;
    if (!terminalNotificationDeduperRef.current.add(notificationKey)) return;

    if (!isResearchDrawingPage(pathname)) {
      const succeeded = status === "succeeded";
      const href = `/research-drawing?taskId=${taskId}`;
      void publishTaskNotification({
        taskType: "research-drawing",
        taskId,
        status,
        title: succeeded ? "科研绘图已完成" : "科研绘图生成失败",
        body: succeeded
          ? "前往「科研绘图」查看，点击立即查看。"
          : "任务未完成，点击查看详情后重试。",
        href,
        ...(!succeeded ? { error } : {}),
      });

      if (shouldPromptDesktopNotificationSettings()) {
        setShowDesktopNotificationSettings(true);
      }
    }

    untrackResearchDrawingTask(taskId);
  }, [pathname]);

  const closeDesktopNotificationSettings = useCallback(() => {
    markDesktopNotificationSettingsPrompted();
    setShowDesktopNotificationSettings(false);
  }, []);

  const handleOpenDesktopNotificationSettings = useCallback(async () => {
    setOpeningDesktopNotificationSettings(true);
    const opened = await openDesktopNotificationSettings();
    setOpeningDesktopNotificationSettings(false);
    if (opened) {
      closeDesktopNotificationSettings();
    }
  }, [closeDesktopNotificationSettings]);

  useEffect(() => {
    if ((state.phase !== "done" && state.phase !== "error") || !state.taskId) {
      return;
    }

    notifyTerminalTask(
      String(state.taskId),
      state.phase === "done" ? "succeeded" : "failed",
      state.error,
    );
  }, [notifyTerminalTask, state.error, state.phase, state.taskId]);

  useEffect(() => {
    if (state.taskId && ["connecting", "created", "generating", "saving"].includes(state.phase)) {
      trackResearchDrawingTask(state.taskId);
    }
  }, [state.phase, state.taskId]);

  useEffect(() => {
    let disposed = false;

    const pollTrackedTasks = async () => {
      const trackedTaskIds = getTrackedResearchDrawingTaskIds();
      if (!trackedTaskIds.length) return;

      await fetchHistory(1);
      if (disposed) return;

      const history = useStore.getState().researchDrawing.history.list;
      history.forEach((task: ImageGenerationTask) => {
        if (!trackedTaskIds.includes(String(task.taskId))) return;
        if (task.status === 2) {
          notifyTerminalTask(String(task.taskId), "succeeded");
        } else if (task.status === -1) {
          notifyTerminalTask(String(task.taskId), "failed");
        }
      });
    };

    void pollTrackedTasks();
    const timer = window.setInterval(() => {
      void pollTrackedTasks();
    }, 5000);

    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [fetchHistory, notifyTerminalTask]);

  return (
    <AlertDialog
      open={showDesktopNotificationSettings}
      onOpenChange={(open) => {
        if (!open) closeDesktopNotificationSettings();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>开启任务完成通知</AlertDialogTitle>
          <AlertDialogDescription>
            前往系统通知设置，为 Findin 开启通知。macOS 请选“横幅”并将“显示预览”设为“始终”；Windows 请允许通知横幅，即可在科研绘图等任务完成后及时看到提醒内容。
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={closeDesktopNotificationSettings}>稍后再说</AlertDialogCancel>
          <Button onClick={handleOpenDesktopNotificationSettings} disabled={openingDesktopNotificationSettings}>
            {openingDesktopNotificationSettings ? "正在打开…" : "前往设置"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
